import {forwardRef, useCallback, useMemo} from 'react';
import {isBoolean} from 'lodash-es';
import {useIntl} from 'react-intl';
import classNames from 'classnames';

import sdk from '@/sdk';
import {WizKnownError} from '@/sdk/common/error';
import * as store from '@/store';
import {deleteTagRep, feLogger} from '@/buried';
import toast, {ToastType} from '@/components/Toast';
import Tag from './Tag';

import {TagData} from './interface';

import styles from './index.module.scss';

const MAX_TAGS_ERROR_MSG = 'too many tags';

async function getTagList(repository: Repository, docGuid: string) {
    try {
        if (repository) {
            const ksServer = await sdk.getKsServer(repository);
            const tags = await ksServer.getTagList(docGuid);
            store.setCurDocTag({
                tags,
            });
        }
    } catch (err) {
        console.error(err);
    }
}
async function createTag(repository: Repository, docGuid: string, name: string) {
    try {
        const ksServer = await sdk.getKsServer(repository);
        await ksServer.createTag(docGuid, [name]);
        return true;
    } catch (error) {
        if (error instanceof WizKnownError) {
            getTagList(repository, docGuid);
            return error.message;
        }
        console.log(error);
        return false;
    }
}

async function deleteTag(repository: Repository, docGuid: string, {name, tagFrom}: TagData) {
    try {
        const ksServer = await sdk.getKsServer(repository);
        await ksServer.deleteTag(docGuid, name);
        getTagList(repository, docGuid);
        const tagMap: any = {1: '1', 2: '0'};
        const type = tagFrom ? tagMap[tagFrom] : '';
        feLogger(deleteTagRep({pageId: docGuid, tagContent: name, type}));
        return true;
    } catch {
        return false;
    }
}

interface TagAreaCompProps {
    curRepository: Repository;
    curDocument?: WizDocument;
    curDocTagData?: {
        tags?: TagData[];
    };
    className?: string;
    editable: boolean;
    rootId?: string;
}

const TagWrapComp = forwardRef(
    ({curRepository, curDocument, curDocTagData, className, editable, rootId}: TagAreaCompProps, ref?: unknown) => {
        const intl = useIntl();

        const docGuid = curDocument?.docGuid || '';

        const tags = useMemo(() => {
            return curDocTagData?.tags || [];
        }, [curDocTagData?.tags]);

        const handleAddTag = useCallback(
            async (name: string) => {
                if (!curRepository) {
                    return null;
                }
                const successMsg = await createTag(curRepository, docGuid, name);
                if (!successMsg || successMsg === MAX_TAGS_ERROR_MSG) {
                    toast({
                        type: ToastType.Error,
                        content: intl.formatMessage({id: 'toastAddTagError'}),
                    });
                    return isBoolean(successMsg) ? null : successMsg;
                }
                const newTags = [
                    ...tags,
                    {
                        name,
                        tagType: 2,
                        tagFrom: 2,
                    },
                ];

                store.setCurDocTag({
                    tags: newTags,
                });
                return null;
            },
            [curRepository, docGuid, tags, intl]
        );

        const handleDeleteTag = useCallback(
            async (tag: TagData) => {
                if (!curRepository) {
                    return;
                }
                const success = deleteTag(curRepository, docGuid, tag);
                if (!success) {
                    toast({
                        type: ToastType.Error,
                        content: intl.formatMessage({id: 'toastDeleteTagError'}),
                    });
                    return;
                }
                const newTags = tags.filter(data => {
                    return data.name !== tag.name;
                });
                store.setCurDocTag({
                    tags: newTags,
                });
            },
            [curRepository, docGuid, tags, intl]
        );

        if (!curDocument) {
            return null;
        }

        return (
            <Tag
                className={classNames(className, styles.TagArea)}
                tags={tags}
                editable={editable}
                docGuid={docGuid}
                onAdd={handleAddTag}
                onDelete={handleDeleteTag}
                rootId={rootId}
            />
        );
    }
);

const TagWrap = store.connect([store.KEYS.CUR_DOCUMENT_TAG])(
    TagWrapComp
);

export default TagWrap;
